import { Label } from "@component/label";
import { Switch } from "@component/switch";
import { Input } from "@component/input";
import { JobCreateRequest } from "@service/employer";
import TextEditor from "@component/rich-editor/v2/text-editor";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import { Company } from "@type/company";
import { isValidEmail } from "@util/string";
import { useState } from "react";

interface Props {
    data: JobCreateRequest
    companyInfo: Company | undefined
    isEditMode?: boolean
    setData: (data: JobCreateRequest) => void;
}

export default function ConfirmSettingsStep(props: Props) {
    const [, userInfo] = useUserProfileContext();
    const { subscriptionActive, isWorkspaceRestricted } = useSubscription();
    const isTrialSubscription = !!subscriptionActive?.is_trial;
    // Edit mode keeps existing jobs editable through the 7-day grace; create
    // mode locks the moment the trial expires. Paid plans ignore entirely.
    const isTrialGate = props.isEditMode
        ? isWorkspaceRestricted
        : !!subscriptionActive?.is_expired;
    const isSubscriptionLimitReached =
        isTrialSubscription && (
            isTrialGate
            || !!subscriptionActive?.is_cv_screening_limit_reached
            || !!subscriptionActive?.is_cv_limit_reached
            || !!subscriptionActive?.is_ai_limit_reached
        );
    const [isEmailNotValid, setIsEmailNotValid] = useState(false);

    // Defaults: the company-level outgoing email, or — for a NEW job on a
    // company that uses per-job outgoing email — the company name + the current
    // user's email.
    let senderName = props.companyInfo?.company_metadata?.outgoing_email?.sender_name;
    let defaultReplyTo = props.companyInfo?.company_metadata?.outgoing_email?.default_reply_to;

    if (!props.isEditMode && props.companyInfo?.company_metadata?.outgoing_email?.is_job_specific_outgoing_email) {
        senderName = props.data.company_name;
        defaultReplyTo = userInfo?.email;
    }

    // Whatever the user has typed always wins — on create AND edit. This block
    // used to run only in edit mode, so on create the fields were recomputed
    // from the defaults on every render and couldn't be edited (Reply-to Email
    // / Sender Name).
    if (props.data.outgoing_email?.sender_name !== undefined) {
        senderName = props.data.outgoing_email.sender_name;
    }
    if (props.data.outgoing_email?.default_reply_to !== undefined) {
        defaultReplyTo = props.data.outgoing_email.default_reply_to;
    }

    return (
        <div className="space-y-8">
            <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Job Settings
                </h2>
                <p className="text-gray-600">
                    Finalize your job settings before publishing.
                </p>
            </div>

            <div className="space-y-12">
                {/* Assessment Expiry Settings */}
                <div className="grid grid-cols-[320px_1fr] gap-8 items-start pb-12 border-b border-border">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900 mb-2">
                            Screening Link Validity
                        </h3>
                        <p className="text-sm text-gray-600">
                            Control how long candidates have to complete their assessments.
                        </p>
                    </div>
                    <div className="space-y-4">
                        {/* Days until assessment link expires */}
                        <div className="space-y-2">
                            <Label
                                htmlFor="application-expiry-days"
                                className="text-sm font-medium"
                            >
                                Days until screening link expires:
                            </Label>
                            <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                    <Input
                                        id="application-expiry-days"
                                        type="number"
                                        value={props.data.application_expiry_days || 7}
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value) || 7;
                                            props.setData({
                                                ...props.data,
                                                application_expiry_days: Math.min(30, Math.max(7, value))
                                            })
                                        }}
                                        className="w-20"
                                        min="7"
                                        max="30"
                                        disabled={props.data.is_collaborator || isSubscriptionLimitReached}
                                    />

                                    <span className="text-sm text-gray-500">days</span>
                                </div>
                                <p className="text-sm text-red-600">
                                    Enter a value between 7-30 days.
                                </p>
                            </div>
                            <p className="text-sm text-gray-500">
                                Candidates can access the screening using the unique link sent to
                                them. After this period, the link will expire and can no longer be
                                used.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Job Outgoing Email Settings */}
                <div className="grid grid-cols-[320px_1fr] gap-8 items-start pb-12 border-b border-border">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900 mb-2">
                            Job Email Settings
                        </h3>
                        <p className="text-sm text-gray-600">
                            Control how candidates see and reply to emails for this job listing.
                        </p>
                    </div>
                    <div className="space-y-6">
                        {/* Sender Name */}
                        <div className="space-y-2">
                            <Label
                                htmlFor="sender-name"
                                className="text-sm font-medium"
                            >
                                Sender Name
                            </Label>
                            <Input
                                id="sender-name"
                                type="text"
                                value={senderName}
                                onChange={(e) => {
                                    props.setData({
                                        ...props.data,
                                        outgoing_email: {
                                            ...props.data.outgoing_email,
                                            sender_name: e.target.value
                                        }
                                    })
                                }}
                                placeholder="ourteam"
                                className="max-w-md"
                                disabled={props.data.is_collaborator || isSubscriptionLimitReached}
                            />
                            <p className="text-sm text-gray-500">
                                This is the name candidates will see in their inbox.
                            </p>
                        </div>

                        {/* Reply-To Email */}
                        <div className="space-y-2 mt-6">
                            <Label
                                htmlFor="reply-to-email"
                                className="text-sm font-medium"
                            >
                                Reply-to Email
                            </Label>
                            <Input
                                id="reply-to-email"
                                type="email"
                                value={defaultReplyTo}
                                onChange={(e) => {
                                    if (!isValidEmail(e.target.value)) {
                                        setIsEmailNotValid(true);
                                    } else {
                                        setIsEmailNotValid(false);
                                    }

                                    props.setData({
                                        ...props.data,
                                        outgoing_email: {
                                            ...props.data.outgoing_email,
                                            default_reply_to: e.target.value
                                        }
                                    })
                                }}
                                placeholder="work@ourteam.ai"
                                className="max-w-md"
                                disabled={props.data.is_collaborator || isSubscriptionLimitReached}
                            />
                            {isEmailNotValid && (
                                <p className="text-sm text-red-500">
                                    Please enter a valid email address.
                                </p>
                            )}
                            <p className="text-sm text-gray-500">
                                Candidate replies will be sent to this email address.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-[320px_1fr] gap-8 items-start">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900 mb-2">
                            Job Controls
                        </h3>
                        <p className="text-sm text-gray-600">
                            Manage automation settings and control the availability of this job.
                        </p>
                    </div>
                    <div className="space-y-6">
                        {/* Auto Rejection Settings */}
                        <div className="space-y-4">
                            <div className="flex items-center space-x-2">
                                <Switch
                                    id="enable-automated-rejections"
                                    checked={props.data.is_enable_auto_rejection}
                                    onCheckedChange={(checked) =>
                                        // @ts-ignore
                                        props.setData((prev) => ({
                                            ...prev,
                                            is_enable_auto_rejection: checked,
                                            days_auto_rejection: checked ? 2 : 0,
                                            rejection_email_body: props.data.rejection_email_body || "",
                                        }))
                                    }
                                    disabled={props.data.is_collaborator || isSubscriptionLimitReached}
                                    className="data-[state=checked]:bg-blue-600"
                                />

                                <Label
                                    htmlFor="enable-automated-rejections"
                                    className="font-medium"
                                >
                                    Enable Auto Rejection
                                </Label>
                            </div>

                            {/* Days until auto-rejection */}
                            {props.data.is_enable_auto_rejection && (
                                <div
                                    className="space-y-2 p-4 bg-gray-50 rounded-lg"
                                    style={{
                                        backgroundColor: "transparent",
                                        paddingRight: "0px",
                                        paddingLeft: "0px",
                                    }}
                                >
                                    <Label
                                        htmlFor="days-until-rejection"
                                        className="text-sm font-medium"
                                    >
                                        Days until auto-rejection:
                                    </Label>
                                    <div className="flex items-center space-x-2">
                                        <Input
                                            id="days-until-rejection"
                                            type="number"
                                            value={props.data.days_auto_rejection || 2}
                                            onChange={(e) =>
                                                // @ts-ignore
                                                props.setData((prev) => ({
                                                    ...prev,
                                                    days_auto_rejection: parseInt(e.target.value) || 2,
                                                }))
                                            }
                                            className="w-20"
                                            disabled={props.data.is_collaborator || isSubscriptionLimitReached}
                                            min="2"
                                        />

                                        <span className="text-sm text-gray-500">days</span>
                                    </div>
                                    <p className="text-sm text-gray-500">
                                        Automatically reject applications that have been inactive for
                                        the specified number of days. This will only affect new
                                        candidates that apply to your job.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Rejection Email Subject - Only show when auto rejection is enabled */}
                        {props.data.is_enable_auto_rejection && (
                            <div className="space-y-4">
                                <div className="space-y-1">
                                    <Label
                                        htmlFor="rejectionSubject"
                                        className="text-base font-medium"
                                    >
                                        Rejection email subject
                                    </Label>
                                    <Input
                                        id="rejectionSubject"
                                        placeholder="An Update To Your Software Engineer Application"
                                        value={props.data.rejection_email_subject}
                                        onChange={(e) =>
                                            // @ts-ignore
                                            props.setData((prev) => ({ ...prev, rejection_email_subject: e.target.value }))
                                        }
                                        disabled={props.data.is_collaborator || isSubscriptionLimitReached}
                                    />

                                    <p className="text-sm text-gray-500">
                                        The subject to send when rejecting candidates.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Rejection Email Contents - Only show when auto rejection is enabled */}
                        {props.data.is_enable_auto_rejection && (
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-base font-medium">
                                        Rejection email contents
                                    </Label>
                                </div>

                                <TextEditor
                                    defaultValue={props.data.rejection_email_body}
                                    onChange={(value) => {
                                        console.log('value', value);
                                        // @ts-ignore
                                        props.setData((prev) => ({ ...prev, rejection_email_body: value }))
                                    }}
                                    disabled={props.data.is_collaborator || isSubscriptionLimitReached}
                                />

                                <p className="text-sm text-gray-500">
                                    You can use the {`{{ first_name }}`} variable to customize the
                                    message for each candidate.
                                </p>
                            </div>
                        )}

                        {/* Disable Applications */}
                        <div className="flex items-start justify-between">
                            <div className="space-y-1">
                                <div className="flex items-center space-x-2">
                                    <Switch
                                        id="disable-applications"
                                        onCheckedChange={(checked) => {
                                            // @ts-ignore
                                            props.setData((prev) => ({ ...prev, job_status: checked ? "Paused" : "Published" }))
                                        }}
                                        disabled={props.data.is_collaborator || isSubscriptionLimitReached}
                                        className="data-[state=checked]:bg-blue-600"
                                    />

                                    <Label htmlFor="disable-applications" className="font-medium">
                                        Pause Job
                                    </Label>
                                </div>
                                <p className="text-sm text-gray-500">
                                    Disable new and existing applications to be submitted for this
                                    job.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
